/**
 * Land package pricing cascade (totals only).
 *
 * Hotel + hotel_service_charge% → Sub hotel
 * Special land_markup addons (e.g. Transportation) + their % → Sub lines
 * + other addons + activities/transfers + cruise + visa + insurance
 * → land_subtotal
 * → backup_cost% → after_backup
 * → overall_service_charge% → after_service
 * → land_profit% → land_before_gst
 * → land_gst% → land_total
 * → tcs% if international → package_total
 *
 * Separate-cascade addons (e.g. Helicopter) are calculated outside land totals.
 */

import { roundMoney } from './money';

export const DEFAULT_PRICING_RATES = {
    hotel_service_charge: 5,
    backup_cost: 4,
    overall_service_charge: 9,
    land_profit: 3,
    land_gst: 5,
    tcs: 2,
    flight_service_charge: 5,
    flight_gst: 18,
    taxes_absorbed: 0,
    transportation_adults_per_unit: 4,
    puja_adults_per_unit: 4,
    darshan_adults_per_unit: 1,
};

export const DEFAULT_ADDON_TYPES = [
    {
        id: 0,
        code: 'transportation',
        name: 'Transportation',
        formula: 'land_markup',
        service_cost_percent: 20,
        gst_percent: null,
    },
    {
        id: 0,
        code: 'helicopter',
        name: 'Helicopter',
        formula: 'separate_cascade',
        service_cost_percent: 3,
        gst_percent: 5,
    },
    {
        id: 0,
        code: 'puja',
        name: 'Puja',
        formula: 'land_markup',
        service_cost_percent: 0,
        gst_percent: null,
    },
    {
        id: 0,
        code: 'darshan',
        name: 'Darshan',
        formula: 'land_markup',
        service_cost_percent: 0,
        gst_percent: null,
    },
];

/** Codes that use ceil(adults / capacity) unit scaling. */
export const BANDED_ADDON_CODES = ['transportation', 'puja', 'darshan'];

const BANDED_CAPACITY_KEYS = {
    transportation: 'transportation_adults_per_unit',
    puja: 'puja_adults_per_unit',
    darshan: 'darshan_adults_per_unit',
};

const BANDED_CAPACITY_DEFAULTS = {
    transportation: 4,
    puja: 4,
    darshan: 1,
};

/** @deprecated Use DEFAULT_PRICING_RATES / rates param */
export const LAND_HOTEL_PROFIT_PERCENT = DEFAULT_PRICING_RATES.hotel_service_charge;
export const LAND_TRANSPORT_PROFIT_PERCENT = 20;
export const LAND_BACKUP_PERCENT = DEFAULT_PRICING_RATES.backup_cost;
export const LAND_SERVICE_PERCENT = DEFAULT_PRICING_RATES.overall_service_charge;
export const LAND_PROFIT_PERCENT = DEFAULT_PRICING_RATES.land_profit;
export const LAND_GST_PERCENT = DEFAULT_PRICING_RATES.land_gst;
export const LAND_TCS_PERCENT = DEFAULT_PRICING_RATES.tcs;

export const HELICOPTER_PROFIT_PERCENT = 3;
export const HELICOPTER_GST_PERCENT = 5;

const resolveRates = (rates = {}) => ({
    ...DEFAULT_PRICING_RATES,
    ...(rates || {}),
});

const resolveAddonTypes = (addonTypes) => {
    if (Array.isArray(addonTypes) && addonTypes.length > 0) {
        return addonTypes.filter((t) => t && t.is_active !== false);
    }
    return DEFAULT_ADDON_TYPES;
};

const normalizeName = (name) => String(name ?? '').trim().toLowerCase();

/**
 * How many banded units for a given adult count.
 * capacity N: 1..N → 1, N+1..2N → 2, …
 */
export const transportUnits = (adults, capacity = 4) => {
    const a = Math.max(0, Math.floor(Number(adults) || 0));
    const c = Math.max(1, Math.floor(Number(capacity) || 4));
    if (a <= 0) return 0;
    return Math.ceil(a / c);
};

/** Alias used for Puja / Darshan / Transport banding. */
export const bandedUnits = transportUnits;

/**
 * Scale a per-unit cost by adult capacity rule.
 * total = unit_cost × units; adult = total / adults.
 */
export const scaleTransportationPricing = ({ unitCost, adults, capacity = 4 }) => {
    const adultsN = Math.max(0, Math.floor(Number(adults) || 0));
    const unit = roundMoney(unitCost);
    const units = transportUnits(adultsN, capacity);
    const total = roundMoney(unit * units);
    const adult = adultsN > 0 ? roundMoney(total / adultsN) : 0;
    return {
        unit_cost: unit,
        units,
        total,
        adult,
        child: 0,
        infant: 0,
    };
};

export const scaleBandedPricing = scaleTransportationPricing;

/** Resolve banded addon code from type match or category/name. */
export const resolveBandedAddonCode = (addon, addonTypes = []) => {
    const t = findAddonType(addon, addonTypes);
    if (t && BANDED_ADDON_CODES.includes(t.code)) {
        return t.code;
    }
    const category = normalizeName(addon?.category);
    if (BANDED_ADDON_CODES.includes(category)) {
        return category;
    }
    const name = normalizeName(addon?.name);
    if (BANDED_ADDON_CODES.includes(name)) {
        return name;
    }
    const types = resolveAddonTypes(addonTypes);
    const byName = types.find((type) =>
        BANDED_ADDON_CODES.includes(type.code) && normalizeName(type.name) === name
    );
    return byName ? byName.code : null;
};

export const isBandedTypeAddon = (addon, addonTypes = []) =>
    resolveBandedAddonCode(addon, addonTypes) !== null;

/** Adults-per-unit capacity from pricing rates for a banded code. */
export const bandedCapacityForCode = (code, pricingRates = {}) => {
    const key = BANDED_CAPACITY_KEYS[code];
    const fallback = BANDED_CAPACITY_DEFAULTS[code] ?? 1;
    if (!key) {
        return fallback;
    }
    return Math.max(1, Number(pricingRates?.[key] ?? fallback) || fallback);
};

/** True if addon matches the transportation special type (by code). */
export const isTransportationTypeAddon = (addon, addonTypes = []) => {
    return resolveBandedAddonCode(addon, addonTypes) === 'transportation';
};

export const isPujaTypeAddon = (addon, addonTypes = []) =>
    resolveBandedAddonCode(addon, addonTypes) === 'puja';

export const isDarshanTypeAddon = (addon, addonTypes = []) =>
    resolveBandedAddonCode(addon, addonTypes) === 'darshan';

/** Percent of base, rounded to nearest rupee (matches business cascade example). */
const percentOf = (base, percent) => {
    return Math.round(roundMoney(base) * percent / 100);
};

/** Percent of base with 2 decimal places (helicopter GST example: 5.15). */
const percentOfMoney = (base, percent) => {
    return roundMoney((roundMoney(base) / 100) * percent);
};

export const quotationPretaxTotal = ({
    landBeforeGst = 0,
    flightFare = 0,
    flightService = 0,
    separateBeforeGst = 0,
} = {}) => {
    return roundMoney(
        roundMoney(landBeforeGst)
        + roundMoney(flightFare)
        + roundMoney(flightService)
        + roundMoney(separateBeforeGst)
    );
};

const landTaxSlice = (landBase, preTaxDiscount, landGstPercent, tcsPercent, tcsIncluded) => {
    const discount = roundMoney(Math.min(Math.max(0, preTaxDiscount), landBase));
    const landAfterDiscount = roundMoney(landBase - discount);
    const landGst = percentOf(landAfterDiscount, landGstPercent);
    const landAfterGst = roundMoney(landAfterDiscount + landGst);
    const tcs = tcsIncluded ? percentOf(landAfterGst, tcsPercent) : 0;
    return {
        pre_tax_discount: discount,
        land_after_discount: landAfterDiscount,
        land_gst: landGst,
        tcs,
        land_total: roundMoney(landAfterGst + tcs),
    };
};

/**
 * Flat amount is a reduction of the customer total. Convert it to the pre-tax
 * land discount that, after the existing GST and TCS steps, produces that reduction.
 */
const preTaxDiscountForFlatAmount = (landBase, flatAmount, landGstPercent, tcsPercent, tcsIncluded) => {
    const desired = roundMoney(flatAmount);
    const baseline = landTaxSlice(landBase, 0, landGstPercent, tcsPercent, tcsIncluded);
    const gstRate = (Number(landGstPercent) || 0) / 100;
    const tcsRate = tcsIncluded ? ((Number(tcsPercent) || 0) / 100) : 0;
    const factor = (1 + gstRate) * (1 + tcsRate);
    let guess = factor > 0 ? roundMoney(desired / factor) : desired;
    if (guess < 0) guess = 0;
    if (guess > landBase) guess = landBase;

    const targetLandTotal = roundMoney(baseline.land_total - desired);
    const matches = (amount) => Math.abs(
        landTaxSlice(landBase, amount, landGstPercent, tcsPercent, tcsIncluded).land_total - targetLandTotal
    ) < 0.001;

    if (matches(guess)) {
        return guess;
    }

    for (let step = 1; step <= 2000; step += 1) {
        const higher = roundMoney(guess + (step * 0.01));
        const lower = roundMoney(guess - (step * 0.01));
        if (higher <= landBase && matches(higher)) {
            return higher;
        }
        if (lower >= 0 && matches(lower)) {
            return lower;
        }
    }

    return guess;
};

/**
 * Discount only the land package (before GST). Flight and separate-cascade GST
 * stay on their full bases.
 * taxesAbsorbed: those taxes are stored and not added to the customer total.
 * An amount discount is the exact reduction in the customer total.
 */
export const applyDiscountBeforeTax = ({
    landBeforeGst = 0,
    tcsIncluded = false,
    landGstPercent = 0,
    tcsPercent = 0,
    flightFare = 0,
    flightService = 0,
    flightServiceAdult = 0,
    flightServiceChild = 0,
    flightServiceInfant = 0,
    adults = 0,
    childs = 0,
    infants = 0,
    flightGstPercent = 0,
    separateLines = [],
    discountType = 'percent',
    discountValue = 0,
    taxesAbsorbed = false,
} = {}) => {
    const landBase = roundMoney(landBeforeGst);
    const resolvedType = discountType === 'amount' ? 'amount' : 'percent';
    const baseline = landTaxSlice(landBase, 0, landGstPercent, tcsPercent, tcsIncluded);
    const maxFlat = taxesAbsorbed ? landBase : baseline.land_total;

    let discountError = null;
    let discountValueResolved = 0;
    let preTaxDiscount = 0;
    let payableReduction = 0;

    if (discountValue === '' || discountValue === null || discountValue === undefined) {
        discountValueResolved = 0;
    } else {
        const numeric = parseFloat(discountValue);
        if (Number.isNaN(numeric)) {
            discountError = 'Enter a valid discount.';
        } else if (numeric < 0) {
            discountValueResolved = roundMoney(numeric);
            discountError = 'Discount cannot be negative.';
        } else {
            discountValueResolved = roundMoney(numeric);
            if (resolvedType === 'percent' && discountValueResolved > 100) {
                discountError = 'Discount percent cannot exceed 100.';
            } else if (landBase <= 0 && discountValueResolved > 0) {
                discountError = 'Enter a land package amount before applying a discount.';
            } else if (resolvedType === 'percent') {
                preTaxDiscount = roundMoney(landBase * discountValueResolved / 100);
                payableReduction = preTaxDiscount;
            } else if (discountValueResolved > maxFlat + 0.001) {
                discountError = taxesAbsorbed
                    ? 'Discount cannot exceed the land package before GST.'
                    : 'Discount cannot exceed the land package total.';
            } else if (taxesAbsorbed) {
                preTaxDiscount = discountValueResolved;
                payableReduction = discountValueResolved;
            } else {
                preTaxDiscount = preTaxDiscountForFlatAmount(
                    landBase,
                    discountValueResolved,
                    landGstPercent,
                    tcsPercent,
                    tcsIncluded
                );
                const taxed = landTaxSlice(landBase, preTaxDiscount, landGstPercent, tcsPercent, tcsIncluded);
                payableReduction = roundMoney(baseline.land_total - taxed.land_total);
            }
        }
    }

    const applied = discountError
        ? landTaxSlice(landBase, 0, landGstPercent, tcsPercent, tcsIncluded)
        : landTaxSlice(landBase, preTaxDiscount, landGstPercent, tcsPercent, tcsIncluded);
    const landAfterDiscount = applied.land_after_discount;
    const landGst = applied.land_gst;
    const tcs = applied.tcs;
    const landTotal = applied.land_total;
    const discountAmount = discountError ? 0 : payableReduction;

    const gstOnService = (service) => roundMoney((roundMoney(service) / 100) * flightGstPercent);
    const flightGstAdult = gstOnService(flightServiceAdult);
    const flightGstChild = gstOnService(flightServiceChild);
    const flightGstInfant = gstOnService(flightServiceInfant);
    const flightGst = roundMoney(
        (parseFloat(adults) || 0) * flightGstAdult
        + (parseFloat(childs) || 0) * flightGstChild
        + (parseFloat(infants) || 0) * flightGstInfant
    );
    const flightFareAmount = roundMoney(flightFare);
    const flightServiceAmount = roundMoney(flightService);
    const flightTotal = roundMoney(flightFareAmount + flightServiceAmount + flightGst);

    const separate_lines = (separateLines || []).map((line) => {
        const before = roundMoney(line.before_gst);
        const gst = percentOfMoney(before, line.gst_percent);
        return {
            code: line.code,
            name: line.name,
            before_gst: before,
            gst_percent: Number(line.gst_percent) || 0,
            gst,
            total: roundMoney(before + gst),
        };
    });
    const separateBeforeGst = separate_lines.reduce(
        (sum, line) => roundMoney(sum + line.before_gst),
        0
    );
    const separateGst = separate_lines.reduce(
        (sum, line) => roundMoney(sum + line.gst),
        0
    );
    const helicopterGst = roundMoney(
        separate_lines.find((line) => line.code === 'helicopter')?.gst || 0
    );

    const undiscountedPretax = roundMoney(
        flightFareAmount + flightServiceAmount + separateBeforeGst
    );
    const priceBeforeTax = roundMoney(landAfterDiscount + undiscountedPretax);
    const taxTotal = roundMoney(landGst + tcs + flightGst + separateGst);
    const customerTotal = taxesAbsorbed
        ? priceBeforeTax
        : roundMoney(priceBeforeTax + taxTotal);

    return {
        land_before_gst: landBase,
        discount_type: resolvedType,
        discount_value: discountValueResolved,
        discount_amount: discountAmount,
        pre_tax_discount: discountError ? 0 : applied.pre_tax_discount,
        discount_error: discountError,
        land_after_discount: landAfterDiscount,
        land_gst_percent: Number(landGstPercent) || 0,
        land_gst: landGst,
        tcs_percent: Number(tcsPercent) || 0,
        tcs_included: !!tcsIncluded,
        tcs,
        land_total: landTotal,
        flight_fare: flightFareAmount,
        flight_service: flightServiceAmount,
        flight_gst_percent: Number(flightGstPercent) || 0,
        flight_gst_adult: flightGstAdult,
        flight_gst_child: flightGstChild,
        flight_gst_infant: flightGstInfant,
        flight_gst: flightGst,
        flight_total: flightTotal,
        separate_lines,
        separate_gst: separateGst,
        helicopter_gst: helicopterGst,
        tax_application: taxesAbsorbed ? 'absorbed' : 'added',
        customer_total: customerTotal,
    };
};

export const emptyLandCascade = () => ({
    hotel_cost: 0,
    hotel_profit: 0,
    hotel_subtotal: 0,
    transportation_cost: 0,
    transportation_profit: 0,
    transportation_subtotal: 0,
    markup_addons: [],
    other_addons_cost: 0,
    activities_cost: 0,
    cruise_cost: 0,
    visa_cost: 0,
    insurance_cost: 0,
    land_subtotal: 0,
    backup_cost: 0,
    after_backup: 0,
    service_cost: 0,
    after_service: 0,
    land_profit: 0,
    land_before_gst: 0,
    land_gst: 0,
    land_total: 0,
    tcs: 0,
    tcsincluded: false,
    package_total: 0,
});

export const emptyHelicopterCascade = () => ({
    helicopter_cost: 0,
    helicopter_profit: 0,
    helicopter_before_gst: 0,
    helicopter_gst: 0,
    helicopter_total: 0,
});

export const emptySeparateCascade = () => ({
    code: '',
    name: '',
    cost: 0,
    profit: 0,
    before_gst: 0,
    gst: 0,
    total: 0,
    service_cost_percent: 0,
    gst_percent: 0,
});

/** Find matching addon type for an addon name (case-insensitive). */
export const findAddonType = (addon, addonTypes = []) => {
    const name = normalizeName(addon?.name);
    if (!name) return null;
    const types = resolveAddonTypes(addonTypes);
    return types.find((t) => normalizeName(t.name) === name) || null;
};

export const isLandMarkupAddon = (addon, addonTypes = []) => {
    const t = findAddonType(addon, addonTypes);
    return t?.formula === 'land_markup';
};

export const isSeparateCascadeAddon = (addon, addonTypes = []) => {
    const t = findAddonType(addon, addonTypes);
    return t?.formula === 'separate_cascade';
};

/** @deprecated Prefer isLandMarkupAddon(addon, addonTypes) */
export const isTransportationAddon = (addon, addonTypes = []) => {
    const t = findAddonType(addon, addonTypes);
    if (t) return t.formula === 'land_markup' && t.code === 'transportation';
    return normalizeName(addon?.name) === 'transportation';
};

/** @deprecated Prefer isSeparateCascadeAddon(addon, addonTypes) */
export const isHelicopterAddon = (addon, addonTypes = []) => {
    const t = findAddonType(addon, addonTypes);
    if (t) return t.formula === 'separate_cascade' && t.code === 'helicopter';
    return normalizeName(addon?.name) === 'helicopter';
};

/**
 * Partition addon totals by configured types.
 * @returns {{ markupByCode: object, separateByCode: object, other: number }}
 */
export const sumAddonTotalsByType = (addons = [], addonTypes = []) => {
    const types = resolveAddonTypes(addonTypes);
    const markupByCode = {};
    const separateByCode = {};
    let other = 0;

    types.forEach((t) => {
        if (t.formula === 'land_markup') {
            markupByCode[t.code] = { type: t, cost: 0 };
        } else if (t.formula === 'separate_cascade') {
            separateByCode[t.code] = { type: t, cost: 0 };
        }
    });

    const nameToType = {};
    types.forEach((t) => {
        nameToType[normalizeName(t.name)] = t;
    });

    (addons || []).forEach((addon) => {
        const total = roundMoney(addon?.pricing?.total);
        const t = nameToType[normalizeName(addon?.name)];
        if (!t) {
            other = roundMoney(other + total);
            return;
        }
        if (t.formula === 'land_markup' && markupByCode[t.code]) {
            markupByCode[t.code].cost = roundMoney(markupByCode[t.code].cost + total);
        } else if (t.formula === 'separate_cascade' && separateByCode[t.code]) {
            separateByCode[t.code].cost = roundMoney(separateByCode[t.code].cost + total);
        } else {
            other = roundMoney(other + total);
        }
    });

    return { markupByCode, separateByCode, other };
};

/** Legacy helper — uses default addon types when none passed. */
export const sumAddonTotals = (addons = [], addonTypes = []) => {
    const { markupByCode, separateByCode, other } = sumAddonTotalsByType(addons, addonTypes);
    return {
        transportation: markupByCode.transportation?.cost || 0,
        helicopter: separateByCode.helicopter?.cost || 0,
        other,
        markupByCode,
        separateByCode,
    };
};

/**
 * Separate-cascade addons (Helicopter-style):
 * cost → service cost % → before_gst → GST % → total
 */
export const calculateSeparateCascades = (input = {}, addonTypes = []) => {
    const { separateByCode } = sumAddonTotalsByType(input.addons, addonTypes);
    const lines = [];

    Object.keys(separateByCode).forEach((code) => {
        const { type, cost } = separateByCode[code];
        const servicePct = Number(type.service_cost_percent ?? 0);
        const gstPct = Number(type.gst_percent ?? 0);
        const profit = percentOfMoney(cost, servicePct);
        const before_gst = roundMoney(cost + profit);
        const gst = percentOfMoney(before_gst, gstPct);
        const total = roundMoney(before_gst + gst);

        lines.push({
            code: type.code,
            name: type.name,
            cost,
            profit,
            before_gst,
            gst,
            total,
            service_cost_percent: servicePct,
            gst_percent: gstPct,
        });
    });

    return lines;
};

/**
 * Legacy single helicopter cascade (from separate lines or defaults).
 */
export const calculateHelicopterCascade = (input = {}, rates = {}, addonTypes = []) => {
    const lines = calculateSeparateCascades(input, addonTypes);
    const heli = lines.find((l) => l.code === 'helicopter')
        || lines.find((l) => normalizeName(l.name) === 'helicopter');

    if (heli) {
        return {
            helicopter_cost: heli.cost,
            helicopter_profit: heli.profit,
            helicopter_before_gst: heli.before_gst,
            helicopter_gst: heli.gst,
            helicopter_total: heli.total,
        };
    }

    // Fallback for empty / missing
    return emptyHelicopterCascade();
};

export const sumItineraryActivitiesAndTransfers = (itineraries = []) => {
    let total = 0;

    (itineraries || []).forEach((day) => {
        (day.activities || []).forEach((activity) => {
            total = roundMoney(total + roundMoney(activity?.pricing?.total));
        });
        (day.transfers || []).forEach((transfer) => {
            total = roundMoney(total + roundMoney(transfer?.pricing?.total));
        });
    });

    return total;
};

/**
 * @param {object} input
 * @param {object} [rates]
 * @param {Array} [addonTypes]
 */
export const calculateLandCascade = (input = {}, rates = {}, addonTypes = []) => {
    const r = resolveRates(rates);
    const hotel_cost = roundMoney(input.hotelCost);
    const cruise_cost = roundMoney(input.cruiseCost);
    const visa_cost = roundMoney(input.visaCost);
    const insurance_cost = roundMoney(input.insuranceCost);
    const tcsincluded = !!input.tcsincluded;

    const { markupByCode, other: other_addons_cost } = sumAddonTotalsByType(input.addons, addonTypes);

    const activities_cost = input.activitiesCost !== undefined
        ? roundMoney(input.activitiesCost)
        : sumItineraryActivitiesAndTransfers(input.itineraries);

    const hotel_profit = percentOf(hotel_cost, r.hotel_service_charge);
    const hotel_subtotal = roundMoney(hotel_cost + hotel_profit);

    const markup_addons = [];
    let markupSubtotalsSum = 0;

    Object.keys(markupByCode).forEach((code) => {
        const { type, cost } = markupByCode[code];
        const percent = Number(type.service_cost_percent ?? 0);
        const profit = percentOf(cost, percent);
        const subtotal = roundMoney(cost + profit);
        markup_addons.push({
            code: type.code,
            name: type.name,
            cost,
            profit,
            subtotal,
            percent,
        });
        markupSubtotalsSum = roundMoney(markupSubtotalsSum + subtotal);
    });

    const transportLine = markup_addons.find((l) => l.code === 'transportation');
    const transportation_cost = transportLine?.cost || 0;
    const transportation_profit = transportLine?.profit || 0;
    const transportation_subtotal = transportLine?.subtotal || 0;

    const land_subtotal = roundMoney(
        hotel_subtotal
        + markupSubtotalsSum
        + other_addons_cost
        + activities_cost
        + cruise_cost
        + visa_cost
        + insurance_cost
    );

    const backup_cost = percentOf(land_subtotal, r.backup_cost);
    const after_backup = roundMoney(land_subtotal + backup_cost);

    const service_cost = percentOf(after_backup, r.overall_service_charge);
    const after_service = roundMoney(after_backup + service_cost);

    const land_profit = percentOf(after_service, r.land_profit);
    const land_before_gst = roundMoney(after_service + land_profit);

    const land_gst = percentOf(land_before_gst, r.land_gst);
    const land_total = roundMoney(land_before_gst + land_gst);

    const tcs = tcsincluded
        ? percentOf(land_total, r.tcs)
        : 0;

    const package_total = roundMoney(land_total + tcs);

    return {
        hotel_cost,
        hotel_profit,
        hotel_subtotal,
        transportation_cost,
        transportation_profit,
        transportation_subtotal,
        markup_addons,
        other_addons_cost,
        activities_cost,
        cruise_cost,
        visa_cost,
        insurance_cost,
        land_subtotal,
        backup_cost,
        after_backup,
        service_cost,
        after_service,
        land_profit,
        land_before_gst,
        land_gst,
        land_total,
        tcs,
        tcsincluded,
        package_total,
    };
};
